<template>
  <div>
    <b-card class="mt-3">
      <b-form>
        <div class="form-group flex-inline-form-md">
          <b-input-group class="fill-width">
            <b-input-group-text slot="prepend">
              <i class="fa fa-search"></i>
            </b-input-group-text>
            <b-form-input type="text" placeholder="Search order by order number" v-model="searchQuery"></b-form-input>
          </b-input-group>
        </div>
      </b-form>
      <b-table striped responsive
        :items="table.items"
        :fields="table.fields">
        <template #cell(number)="data">
          <router-link :to="`/shipment_md/orders/${data.item.id}`">{{data.value}}</router-link>
        </template>
        <template #cell(shipment)="data">
          <router-link :to="`/shipment_md/shipments/${getShipmentId(data.item.shipment)}`">{{getShipmentNumber(data.item.shipment)}}</router-link>
        </template>
        <template #cell(customer)="data">
          <div>
            <div>{{data.value.name}}</div>
            <template v-if="data.value.address">
              <div class="small text-muted">{{data.value.address.address1}}</div>
              <div class="small text-muted">{{data.value.address.city}} {{data.value.address.state}} {{data.value.address.zip_code}}</div>
              <div v-if="!data.item.destination" class="text-danger small">
                <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                Invalid destination
              </div>
            </template>
          </div>
        </template>
        <template #cell(delivery_date)="data">{{formatDate(data.value)}}</template>
        <template #cell(time_in)="data">
          <div v-b-tooltip.hover title="Time In GPS" class="text-muted small">
            <i class="fa fa-truck"></i>
            {{formatDateTime(getTimeIn(data.item.metadata)) || 'Unknown'}}
          </div>
          <div v-b-tooltip.hover title="Time In App" class="text-muted small">
            <i class="fa fa-android"></i>
            {{formatDateTime(getTimeIn(data.item.metadata, 'app')) || 'Unknown'}}
          </div>
        </template>
        <template #cell(time_out)="data">
          <div v-b-tooltip.hover title="Time Out GPS" class="text-muted small">
            <i class="fa fa-truck"></i>
            {{formatDateTime(getTimeOut(data.item.metadata)) || 'Unknown'}}
          </div>
          <div v-b-tooltip.hover title="Time Out App" class="text-muted small">
            <i class="fa fa-android"></i>
            {{formatDateTime(getTimeOut(data.item.metadata, 'app')) || 'Unknown'}}
          </div>
        </template>
        <template #cell(duration)="data">
          <div v-if="getDuration(data.item.metadata)" v-b-tooltip.hover title="Duration GPS" class="text-muted small">
            <i class="fa fa-truck"></i>
            {{getDuration(data.item.metadata)}}
          </div>
          <div v-if="getDuration(data.item.metadata, 'app')" v-b-tooltip.hover title="Duration App" class="text-muted small">
            <i class="fa fa-android"></i>
            {{getDuration(data.item.metadata, 'app')}}
          </div>
        </template>
        <template #cell(status)="data">
          <div v-if="!isObjectEmpty(getConfirmation(data.item))">
            <div class="text-success"><i class="fa fa-user" aria-hidden="true"></i>
              {{getConfirmation(data.item).name}}
            </div>
            <div class="text-muted small"><i class="fa fa-calendar" aria-hidden="true"></i>
              {{formatDateTime(getConfirmation(data.item).created_at)}}
            </div>
          </div>
          <div v-if="isObjectEmpty(getConfirmation(data.item)) && getOrderShipmentType(data.item) != 'ON_CALL'" class="text-muted">Unconfirmed</div>
          <div v-if="getOrderShipmentType(data.item) === 'ON_CALL'" class="text-muted small">{{ getCurrentDeliveryStatus(data.item) }}</div>
        </template>
        <template #cell(type)="data">
          {{ getOrderShipmentType(data.item).replace('_', ' ') }}
        </template>
        <template #cell(action)="data">
          <div>
            <b-button
              title="Show Last Position"
              class="mb-2"
              size="sm"
              v-if="getOrderShipmentType(data.item) !== 'ON_CALL' && data.item.shipment !== null"
              variant="outline-primary"
              @click="showMap(data.item)">
              <i class="fa icon-map" aria-hidden="true"></i>&nbsp;
              Show Last Position
            </b-button>
            <b-button
              title="Update Status"
              class="mb-2"
              size="sm"
              v-if="getOrderShipmentType(data.item) === 'ON_CALL'"
              variant="outline-primary" @click="showModalForm(data.item)">
              <i class="fa fa-pencil-square-o" aria-hidden="true"></i>&nbsp;
              Update Status
            </b-button>
          </div>
        </template>
      </b-table>
    </b-card>
    <nav aria-label="Shipment pagination" class="pull-right">
      <ul class="pagination">
        <li class="page-item" :class="{disabled: prevDisabled}">
          <a class="page-link" href="#" @click.prevent="prevPage()"><span aria-hidden="true">&laquo;</span> Previous</a>
        </li>
        <li class="page-item" :class="{disabled: nextDisabled}">
          <a class="page-link" href="#" @click.prevent="nextPage()">Next <span aria-hidden="true">&raquo;</span></a>
        </li>
      </ul>
    </nav>
    <b-modal @hide="onCloseModalForm" ref="modal-form" id="order-status-modal" size="lg" title="Order Status">
      <div class="form-row">
        <div class="form-group col-md-6">
          <label class="pull-left" for="input_end_date_time">Date and Time</label>
          <flat-pickr class="form-control"
            v-model="datetime"
            :config="config"></flat-pickr>
        </div>
        <div class="form-group col-md-6">
          <label class="pull-left" for="status-select">Status</label>
          <b-form-select
            id="status-select"
            :state="validationStatus"
            v-model="selectedStatus"
            :options="statusList"
          ></b-form-select>
          <b-form-invalid-feedback class="invalid-status-feedback" :state="validationStatus">Select status</b-form-invalid-feedback>
        </div>
      </div>
      <div v-if="useConfirmField" class="form-row">
        <div class="form-group col-md-6">
          <label class="pull-left" for="status-select">Notes</label>
          <b-form-input v-model="confirmNotes" placeholder="Notes"></b-form-input>
        </div>
      </div>
      <div class="form-row">
        <div class="form-group col-md-12">
          <b-list-group>
            <b-list-group-item v-for="(status, s_idx) in get(selectedOrder, 'delivery_status', [])" :key="s_idx" class="d-flex justify-content-between align-items-center p-2">
              <p class="mb-0"><b>{{ formatDateTime(get(status, 'datetime', '')) }}</b> &bull; <span class="text-muted">{{ get(status, 'name', '') }}</span></p>
              <b-badge
                title="Remove this status"
                @click="removeConfirmation"
                class="btn-remove-confirmation"
                v-if="get(status, 'name', '').indexOf('diterima') !== -1"
                variant="danger" pill>remove</b-badge>
            </b-list-group-item>
          </b-list-group>
        </div>
      </div>
      <template #modal-footer>
        <div class="w-100">
          <b-button
            :disabled="loadingSubmit"
            @click="onSubmit"
            variant="primary"
            size="sm"
            class="float-right">
            {{ buttonText }}
          </b-button>
        </div>
      </template>
    </b-modal>
    <b-modal @hide="onCloseModal" ref="modal" size="lg" title="Order Last Position" ok-only>
      <table cellpadding="3" class="mb-3 table-status">
        <tr><td>Order Number</td><td>:</td><td class="pull-left"><strong>{{ get(selectedOrder, 'number', '-') }}</strong></td></tr>
        <tr><td>Current Status</td><td>:</td><td class="pull-left"><strong>{{ getCurrentDeliveryStatus(selectedOrder) }}</strong></td></tr>
        <tr><td>Driver</td><td>:</td><td class="pull-left"><strong>{{ get(selectedOrder, 'shipment.vehicle.driver.name', '-') }}</strong></td></tr>
        <tr><td>Address</td><td>:</td><td class="pull-left"><strong>{{ get(currentPosition, 'roadname', '-') }}</strong></td></tr>
      </table>
      <b-card class="map-container mb-0">
        <div v-if="mapState === 'loading'">
          <p style="margin-bottom: 0px;">Please wait...</p>
        </div>
        <div v-if="mapState === 'ready'">
        <l-map
          ref="map"
          @ready="onMapReady"
          :style="getMapStyles()"
          :min-zoom="1"
          :max-zoom="20"
          :bounds="bounds"
          :zoom.sync="zoom"
          :center="center">
          <l-tile-layer :url="url"></l-tile-layer>
          <l-marker
            :lat-lng="getMarkerPoint()"
            :icon="getMarkerIcon()">
            <l-tooltip :options="tooltipOptions">
              <p style="margin-bottom: 0px;">{{ get(selectedOrder, 'shipment.vehicle.reg_no', '') }}</p>
            </l-tooltip>
          </l-marker>
        </l-map>
        </div>
      </b-card>
    </b-modal>
  </div>
</template>

<script>
import dateMixin from '@/mixins/date';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import L from 'leaflet';
import { LMap, LTileLayer, LMarker, LTooltip } from 'vue2-leaflet';

import { last, throttle, isObject, isEmpty, get } from 'lodash';

export default {
  name: 'order_status',
  mixins: [dateMixin],
  components: {
    flatPickr,
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
  },
  data() {
    return {
      lastData: null,
      lastDataAr: [],
      searchQuery: '',
      selectedStatus: '0',
      statusList: [
        {
          text: 'Select status',
          value: '0',
          disabled: true,
        },
        {
          text: 'Paket sudah keluar dari gudang',
          value: 'depart_for_delivery',
        },
        {
          text: 'Paket sudah tiba di area pengiriman',
          value: 'arrived_at_customer',
        },
        {
          text: 'Paket sudah diterima customer',
          value: 'order_confirmed',
        },
      ],
      config: {
        enableTime: true,
        enableSeconds: true,
        allowInput: true,
      },
      table: {
        fields: [
          'number',
          'shipment',
          'customer',
          'delivery_date',
          'time_in',
          'time_out',
          'duration',
          'status',
          'type',
          {
            key: 'action',
            class: 'action',
          },
        ],
        items: [],
      },
      bounds: L.latLngBounds(
        { lat: 51.476483373501964, lng: -0.14668464136775586 },
        { lat: 51.52948330894063, lng: -0.019140238291583955 },
      ),
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      center: [-6.17543846855061, 106.86290799928327],
      zoom: 13,
      mapState: 'loading',
      selectedOrder: {},
      currentPosition: {},
      tooltipOptions: {
        permanent: true,
        direction: 'top',
        offset: L.point(0, -30),
      },
      datetime: new Date(),
      confirmNotes: '',
      buttonText: 'Submit',
      loadingSubmit: false,
    };
  },
  computed: {
    orders() {
      return this.$store.getters['shipment_md/order_status/order_status'];
    },
    prevDisabled() {
      return this.lastData === null;
    },
    nextDisabled() {
      return this.orders.length < 10;
    },
    useConfirmField() {
      return this.selectedStatus === 'order_confirmed';
    },
    validationStatus() {
      return this.selectedStatus !== '0';
    },
  },
  created() {
    this.$store.commit('SET_BREADCRUMB_ITEMS', [
      { to: '', name: 'Home' },
      { to: 'shipment_md', name: 'Shipment MD' },
      { to: 'orders', name: 'Orders' },
      { to: 'status', name: 'Order Status' },
    ]);
    this.loadData();
  },
  watch: {
    searchQuery(newValue) {
      this.updateSearchQuery(this, newValue);
    },
    orders(data) {
      this.table.items.splice(0);
      data.forEach((d) => {
        this.table.items.push(d);
      });
    },
  },
  methods: {
    get,
    showModalForm(order) {
      this.$refs['modal-form'].show();
      this.selectedOrder = order;
      this.datetime = new Date();
    },
    async showMap(order) {
      this.$refs.modal.show();
      this.mapState = 'loading';
      this.selectedOrder = order;

      const gpsNumber = get(order, 'shipment.vehicle.gps_devices.0.number', null);

      if (gpsNumber) {
        try {
          this.currentPosition = await this.$store.dispatch('gpsDevices/GET_GPS_REALTIME', { number: gpsNumber });
        } catch (error) {
          console.log('error fetch position', error);
        }
      }

      if (get(this.currentPosition, 'alat', null)) {
        this.bounds = L.latLngBounds([{ lat: get(this.currentPosition, 'alat', 0), lng: get(this.currentPosition, 'along', 0) }]);
      }

      setTimeout(() => {
        this.mapState = 'ready';
      }, 5e2);
    },
    onMapReady() {
      setTimeout(() => {
        this.$refs.map.mapObject.invalidateSize();
        if (this.currentPosition.alat) {
          this.$refs.map.setZoom(18);
        }
      }, 1e2);
    },
    getMapStyles() {
      const height = window.innerHeight - 400;
      const styles = `height: ${height}px; width: 100%;`;
      return styles;
    },
    loadData() {
      this.$store.commit('shipment_md/order_status/SET_QUERY_LAST_DATA', this.lastData);
      if (this.searchQuery.trim() !== '') {
        return this.$store.dispatch('shipment_md/order_status/FIND_DATA');
      }
      return this.$store.dispatch('shipment_md/order_status/LOAD_DATA');
    },
    prevPage() {
      this.lastDataAr.pop();
      if (this.lastDataAr.length) {
        this.lastData = last(this.lastDataAr);
      } else {
        this.lastData = null;
      }
      this.loadData();
    },
    nextPage() {
      const order = last(this.orders);
      this.lastDataAr.push(order.id);
      this.lastData = order.id;
      this.loadData();
    },
    resetPagination() {
      this.lastData = null;
      this.lastDataAr = [];
    },
    updateSearchQuery: throttle((context, searchTerm) => {
      const searchQuery = searchTerm.trim();
      context.resetPagination();
      context.$store.commit('shipment_md/order_status/SET_QUERY_SEARCH', searchQuery);
      context.loadData();
    }, 500, {
      leading: false,
    }),
    getShipmentNumber(shipment) {
      if (isObject(shipment)) {
        return shipment.number;
      }
      return undefined;
    },
    getShipmentId(shipment) {
      if (isObject(shipment)) {
        return shipment.id;
      }
      return undefined;
    },
    getConfirmation(data) {
      return data.confirmations.find(c => c.key === 'customer') || {};
    },
    isObjectEmpty(object) {
      return isEmpty(object);
    },
    getTimeIn(metadata, source = 'gps') {
      if (isObject(metadata)) {
        const sourceKey = source === 'gps' ? 'time_in' : 'time_in_app';
        return metadata[sourceKey] || '-';
      }
      return undefined;
    },
    getTimeOut(metadata, source = 'gps') {
      if (isObject(metadata)) {
        const sourceKey = source === 'gps' ? 'time_out' : 'time_out_app';
        return metadata[sourceKey] || '-';
      }
      return undefined;
    },
    getDuration(metadata, source = 'gps') {
      const timeIn = this.getTimeIn(metadata, source);
      const timeOut = this.getTimeOut(metadata, source);
      const durationInMinutes = this.getDurationValueInMinutes(timeOut, timeIn);

      const minutesInADay = 1440;
      const isMoreThanOneDay = durationInMinutes > minutesInADay;
      const durationHumanize = isMoreThanOneDay ? 'more than 1day' : `${durationInMinutes} minutes`;

      return durationInMinutes > 0 ? durationHumanize : undefined;
    },
    getOrderShipmentType(order) {
      const shipmentType = get(order, 'metadata.shipmentType', 'reguler');
      return shipmentType.toUpperCase();
    },
    getMarkerPoint() {
      const alat = get(this.currentPosition, 'alat', 0);
      const along = get(this.currentPosition, 'along', 0);
      return {
        lat: parseFloat(alat),
        lng: parseFloat(along),
      };
    },
    getMarkerIcon() {
      const icon = L.icon({
        iconUrl: 'https://unpkg.com/leaflet@1.2.0/dist/images/marker-icon.png',
        iconSize: [25, 41],
        iconAnchor: [12.5, 32],
      });
      return icon;
    },
    onCloseModal() {
      this.currentPosition = {};
      this.selectedOrder = {};
    },
    onCloseModalForm() {
      this.selectedOrder = {};
      this.selectedStatus = '0';
      this.confirmNotes = '';
      this.loadingSubmit = false;
    },
    getCurrentDeliveryStatus(order) {
      let status = '-';
      const deliveryStatus = get(order, 'delivery_status', []);

      if (!isEmpty(deliveryStatus)) {
        const len = deliveryStatus.length;
        status = deliveryStatus[len - 1].name;
      }

      return status;
    },
    onSubmit() {
      const {
        selectedOrder, selectedStatus, confirmNotes, datetime,
      } = this;
      const formatedDate = new Date(datetime).toISOString();
      const orderId = get(selectedOrder, 'id', null);
      const shipmentId = get(selectedOrder, 'shipment.id', null);

      let method = '';
      let payloads = {};

      if (selectedStatus === 'depart_for_delivery') {
        method = 'shipment_md/shipments/UPDATE_DATA';
        payloads = {
          id: shipmentId,
          metadata: {
            time_out: formatedDate,
          },
        };
      } else if (selectedStatus === 'arrived_at_customer') {
        method = 'shipment_md/orders/UPDATE_DATA';
        payloads = {
          id: orderId,
          metadata: {
            time_in: formatedDate,
          },
        };
      } else if (selectedStatus === 'order_confirmed') {
        method = 'shipment_md/orders/FINISH_ORDER';
        payloads = {
          order_id: orderId,
          reason: confirmNotes,
        };
      } else if (selectedStatus === '0') {
        return Promise.resolve();
      }

      this.buttonText = 'Processing...';
      this.loadingSubmit = true;

      return this.$store.dispatch(method, payloads)
        .then(() => {
          this.loadingSubmit = false;
          this.buttonText = 'Success';
          this.loadData();

          setTimeout(() => {
            this.buttonText = 'Submit';
            this.$refs['modal-form'].hide();
          }, 1e3);
        })
        .catch(() => {
          this.loadingSubmit = false;
          this.buttonText = 'Failed';
          this.loadData();

          setTimeout(() => {
            this.buttonText = 'Submit';
            this.$refs['modal-form'].hide();
          }, 1e3);
        });
    },
    removeConfirmation() {
      const confirmationId = get(this.selectedOrder, 'confirmations.0.id', null);
      if (confirmationId) {
        this.$store.dispatch('shipment_md/order_status/REMOVE_ORDER_CONFIRMATION', { id: confirmationId }).then(() => {
          const delIndex = this.selectedOrder.delivery_status.findIndex(dt => dt.name.indexOf('diterima') !== -1);
          this.selectedOrder.delivery_status.splice(delIndex, 1);
          this.loadData();

          setTimeout(() => {
            this.$refs['modal-form'].hide();
          }, 1e3);
        });
      }
    },
  },
};
</script>

<style>
  .action {
    min-width: 200px;
  }
  .btn-remove-confirmation {
    cursor: pointer;
  }
  .invalid-status-feedback {
    text-align: left;
  }
</style>

<style lang="sass" scoped>
  @import "~leaflet/dist/leaflet.css";
</style>


// WEBPACK FOOTER //
// src/views/pages/shipment_md/orders/OrderStatus.vue